// hooks/useMuseumObjects.js - Custom hook for museum objects CRUD operations
import { useState, useCallback } from 'react';
import {
  createObject,
  updateObject,
  deleteObject,
  getObject,
  getObjectByNumber,
  getAllObjects,
  getObjectsByRoom,
  getNextObjectNumber,
  isObjectNumberAvailable,
  searchObjects,
  getObjectCountsByRoom,
  bulkUpdateObjects
} from '../MuseumObjects/utils/objectsDatabase';

export const useMuseumObjects = (museumId) => {
  const [objects, setObjects] = useState([]);
  const [currentObject, setCurrentObject] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    room: '',
    category: '',
    searchTerm: ''
  });

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Fetch all objects with optional filters
  const fetchObjects = useCallback(async (filterOptions = {}) => {
    if (!museumId) {
      setObjects([]);
      return [];
    }

    setIsLoading(true);
    setError(null);

    try {
      const fetchedObjects = await getAllObjects(museumId, filterOptions);
      setObjects(fetchedObjects);
      return fetchedObjects;
    } catch (err) {
      console.error('Failed to fetch objects:', err);
      setError(err.message);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [museumId]);

  // Fetch a single object by ID
  const fetchObject = useCallback(async (objectId) => {
    if (!museumId) return null;

    setIsLoading(true);
    setError(null);

    try {
      const obj = await getObject(museumId, objectId);
      setCurrentObject(obj);
      return obj;
    } catch (err) {
      console.error('Failed to fetch object:', err);
      setError(err.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [museumId]);

  // Fetch object by visitor-facing number
  const fetchObjectByNumber = useCallback(async (objectNumber) => {
    if (!museumId) return null;

    setIsLoading(true);
    setError(null);

    try {
      const obj = await getObjectByNumber(museumId, objectNumber);
      return obj;
    } catch (err) {
      console.error('Failed to fetch object by number:', err);
      setError(err.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [museumId]);

  // Create a new object
  const handleCreateObject = useCallback(async (objectData) => {
    if (!museumId) {
      setError('No museum selected');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Check if object number is available
      const numberAvailable = await isObjectNumberAvailable(museumId, objectData.objectNumber);
      if (!numberAvailable) {
        throw new Error(`Object number ${objectData.objectNumber} is already in use`);
      }

      const result = await createObject(museumId, objectData);

      // Refresh objects list
      await fetchObjects(filters);

      return result;
    } catch (err) {
      console.error('Failed to create object:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [museumId, fetchObjects, filters]);

  // Update an object
  const handleUpdateObject = useCallback(async (objectId, updates) => {
    if (!museumId) {
      setError('No museum selected');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      // If updating object number, check availability
      if (updates.objectNumber !== undefined) {
        const numberAvailable = await isObjectNumberAvailable(
          museumId,
          updates.objectNumber,
          objectId
        );
        if (!numberAvailable) {
          throw new Error(`Object number ${updates.objectNumber} is already in use`);
        }
      }

      await updateObject(museumId, objectId, updates);

      // Refresh the object data
      const updated = await getObject(museumId, objectId);
      setCurrentObject(updated);

      // Update in the list
      setObjects(prev =>
        prev.map(obj => obj.id === objectId ? updated : obj)
      );

      return { success: true };
    } catch (err) {
      console.error('Failed to update object:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [museumId]);

  // Delete an object
  const handleDeleteObject = useCallback(async (objectId) => {
    if (!museumId) {
      setError('No museum selected');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      await deleteObject(museumId, objectId);

      // Remove from list
      setObjects(prev => prev.filter(obj => obj.id !== objectId));

      // Clear current if it was the deleted one
      if (currentObject?.id === objectId) {
        setCurrentObject(null);
      }

      return { success: true };
    } catch (err) {
      console.error('Failed to delete object:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [museumId, currentObject]);

  // Get next available object number
  const getNextNumber = useCallback(async () => {
    if (!museumId) return 1;

    try {
      return await getNextObjectNumber(museumId);
    } catch (err) {
      console.error('Failed to get next object number:', err);
      return 1;
    }
  }, [museumId]);

  // Check if object number is available
  const checkNumberAvailable = useCallback(async (objectNumber, excludeObjectId = null) => {
    if (!museumId) return false;

    try {
      return await isObjectNumberAvailable(museumId, objectNumber, excludeObjectId);
    } catch (err) {
      console.error('Failed to check number availability:', err);
      return false;
    }
  }, [museumId]);

  // Search objects
  const handleSearchObjects = useCallback(async (searchTerm) => {
    if (!museumId) {
      setObjects([]);
      return [];
    }

    if (!searchTerm || searchTerm.trim() === '') {
      return fetchObjects(filters);
    }

    setIsLoading(true);
    setError(null);

    try {
      const results = await searchObjects(museumId, searchTerm);
      setObjects(results);
      return results;
    } catch (err) {
      console.error('Failed to search objects:', err);
      setError(err.message);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [museumId, fetchObjects, filters]);

  // Fetch objects by room
  const fetchObjectsByRoom = useCallback(async (roomId) => {
    if (!museumId) {
      setObjects([]);
      return [];
    }

    setIsLoading(true);
    setError(null);

    try {
      const fetchedObjects = await getObjectsByRoom(museumId, roomId);
      setObjects(fetchedObjects);
      return fetchedObjects;
    } catch (err) {
      console.error('Failed to fetch objects by room:', err);
      setError(err.message);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [museumId]);

  // Get object counts by room
  const getCountsByRoom = useCallback(async () => {
    if (!museumId) return {};

    try {
      return await getObjectCountsByRoom(museumId);
    } catch (err) {
      console.error('Failed to get object counts:', err);
      return {};
    }
  }, [museumId]);

  // Bulk update objects
  const handleBulkUpdate = useCallback(async (objectIds, updates) => {
    if (!museumId) {
      setError('No museum selected');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await bulkUpdateObjects(museumId, objectIds, updates);

      // Refresh objects list
      await fetchObjects(filters);

      return result;
    } catch (err) {
      console.error('Failed to bulk update objects:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [museumId, fetchObjects, filters]);

  // Update filters and refetch
  const updateFilters = useCallback(async (newFilters) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);

    // Build filter object for API (exclude empty values and searchTerm)
    const apiFilters = {};
    if (updatedFilters.room) apiFilters.room = updatedFilters.room;
    if (updatedFilters.category) apiFilters.category = updatedFilters.category;

    // If there's a search term, use search; otherwise use filters
    if (updatedFilters.searchTerm) {
      await handleSearchObjects(updatedFilters.searchTerm);
    } else {
      await fetchObjects(apiFilters);
    }
  }, [filters, fetchObjects, handleSearchObjects]);

  // Clear filters
  const clearFilters = useCallback(async () => {
    setFilters({ room: '', category: '', searchTerm: '' });
    await fetchObjects({});
  }, [fetchObjects]);

  // Select an object (from already fetched list)
  const selectObject = useCallback((objectId) => {
    const obj = objects.find(o => o.id === objectId);
    setCurrentObject(obj || null);
    return obj;
  }, [objects]);

  // Create new object with auto-assigned number
  const initNewObject = useCallback(async () => {
    const nextNumber = await getNextNumber();
    return {
      objectNumber: nextNumber,
      name: '',
      description: '',
      photoUrl: '',
      room: '',
      floor: 1,
      category: '',
      artist: '',
      period: '',
      materials: [],
      dimensions: '',
      inventoryNumber: '',
      featured: false,
      visible: true
    };
  }, [getNextNumber]);

  return {
    // State
    objects,
    currentObject,
    isLoading,
    error,
    filters,

    // Actions
    fetchObjects,
    fetchObject,
    fetchObjectByNumber,
    fetchObjectsByRoom,
    createObject: handleCreateObject,
    updateObject: handleUpdateObject,
    deleteObject: handleDeleteObject,
    selectObject,
    setCurrentObject,
    clearError,

    // Number management
    getNextNumber,
    checkNumberAvailable,

    // Search & Filter
    searchObjects: handleSearchObjects,
    updateFilters,
    clearFilters,

    // Statistics
    getCountsByRoom,

    // Bulk operations
    bulkUpdate: handleBulkUpdate,

    // Helpers
    initNewObject
  };
};

export default useMuseumObjects;
